import { PackageSearch } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";
import { useNavigate } from "react-router-dom";

import Pagination from "../common/Pagination";
import api from "../../../configs/api";
import useDebounce from "../../../Hooks/useDebounce";
import { useEntityModal } from "../../../Hooks/useEntityModal";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";
import {
  FaMapMarkerAlt,
  FaBriefcase,
  FaRupeeSign,
  FaCalendarAlt,
} from "react-icons/fa";

import EditCarrierModal from "./EditCarrierModal";

/*  Carrier card  */
const CarrierCard = ({ carrier, onEdit, onDelete }) => {
  return (
    <div
      className="group border border-borderLight/60 bg-surfaceColor overflow-hidden relative flex flex-col
hover:shadow-[0_12px_28px_rgba(0,0,0,0.12)] 
transition-all duration-200"
    >
      {/* CONTENT */}
      <div className="p-4 flex-1 flex flex-col gap-2 cursor-pointer">
        {/* NAME */}
        <h3 className="text-sm font-semibold text-headingText line-clamp-1">
          {carrier?.name}
        </h3>

        {/* DESCRIPTION */}
        <p className="text-xs text-descriptionText line-clamp-2">
          {carrier?.description}
        </p>

        <p className="text-sm capitalize text-descriptionText">
          {carrier?.time}
        </p>

        {/* EXTRA INFO */}
        <div className="text-xs text-descriptionText space-y-1 mt-2 uppercase">
          <p className="flex items-center gap-2">
            <FaMapMarkerAlt className="text-primaryAccent" />
            {carrier?.location || "N/A"}
          </p>

          <p className="flex items-center gap-2">
            <FaBriefcase className="text-primaryAccent" />
            {carrier?.experience || "N/A"}
          </p>

          <p className="flex items-center gap-2">
            <FaRupeeSign className="text-primaryAccent" />
            {carrier?.salary || "N/A"}
          </p>

          <p className="flex items-center gap-2">
            <FaCalendarAlt className="text-primaryAccent" />
            {carrier?.date
              ? new Date(carrier.date).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "N/A"}
          </p>
        </div>
      </div>

      {/* ACTION BUTTONS */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-end gap-3 h-12 px-3 bg-gradient-to-l from-black/50 via-black/10 to-transparent border-t border-white/10 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-300">
        <button
          title="Edit"
          onClick={() => onEdit(carrier?._id)}
          className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white border border-white/20"
        >
          <FiEdit size={14} />
        </button>

        <button
          title="Delete"
          onClick={() => onDelete(carrier)}
          className="w-8 h-8 flex items-center justify-center bg-red-500/70 hover:bg-red-500 text-white border border-red-400/40"
        >
          <FiTrash2 size={14} />
        </button>
      </div>
    </div>
  );
};

/*  Main page */
const Carrier = () => {
  const [carriers, setCarriers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);

  const limit = 8;
  const debouncedSearch = useDebounce(searchInput, 350);
  const carrierModal = useEntityModal();
  const abortRef = useRef(null);
  const navigate = useNavigate();

  const fetchCarriers = useCallback(async () => {
    // Cancel previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      const res = await api.get(
        `/carriers?admin=true&page=${page}&limit=${limit}&search=${debouncedSearch}`,
        { signal: controller.signal },
      );
      const { carriers, pagination } = res.data.data;
      setCarriers(carriers);
      setPagination(pagination);
    } catch (error) {
      if (error.name !== "CanceledError" && error.name !== "AbortError") {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchCarriers();
    return () => abortRef.current?.abort();
  }, [fetchCarriers]);

  // Reset to page 1 whenever search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleEdit = useCallback((carrierId) => {
    carrierModal.openEdit(carrierId);
  }, []);

  const handleCarrierUpdate = useCallback((updatedCarrier) => {
    setCarriers((prev) =>
      prev.map((p) =>
        p._id === updatedCarrier._id ? { ...p, ...updatedCarrier } : p,
      ),
    );
  }, []);

  const handleDelete = useCallback(
    async (carrier) => {
      const confirmed = await confirmAction({
        title: "Delete Carrier?",
        text: `This will permanently delete "${carrier.name}".`,
        confirmText: "Delete",
        cancelText: "Cancel",
      });

      if (!confirmed) return;

      const previousCarriers = carriers;

      try {
        /* OPTIMISTIC UPDATE */
        setCarriers((prev) => prev.filter((p) => p._id !== carrier._id));

        if (pagination?.total) {
          setPagination((prev) => ({
            ...prev,
            total: prev.total - 1,
          }));
        }

        /* API CALL */
        await api.delete(`/carriers/${carrier._id}`);

        showSuccess("Carrier deleted successfully");
      } catch (error) {
        console.error(error);

        setCarriers(previousCarriers);

        showError("Failed to delete carrier");
      }
    },
    [carriers, pagination],
  );

  const total = pagination?.total ?? null;

  return (
    <div className="">
      {/* ACTION BAR */}
      <div className="flex items-center justify-between mb-6 gap-3">
        {/* SEARCH */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-descriptionText pointer-events-none">
            <IoIosSearch className="size-4" />
          </span>
          <input
            type="text"
            placeholder="Search carriers…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-[42px] w-[25rem] pl-9 pr-4 border border-borderLight outline-none focus:border-primaryAccent text-sm bg-white transition-colors"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-descriptionText hover:text-headingText text-xs"
            >
              ✕
            </button>
          )}
        </div>

        {/* RIGHT CONTROLS */}
        <div className="flex items-center gap-2">
          <button
            onClick={carrierModal.openCreate}
            className="h-[42px] px-5 bg-btnColor text-white text-sm  font-medium  tracking-wide hover:bg-btnHoverColor transition-colors"
          >
            + Create Carrier
          </button>
        </div>
      </div>
      {/* GRID or SKELETON */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {loading ? (
          Array.from({ length: limit }).map((_, i) => <SkeletonCard key={i} />)
        ) : !loading && carriers?.length === 0 ? (
          <EmptyState search={debouncedSearch} />
        ) : (
          carriers?.map((carrier) => (
            <CarrierCard
              key={carrier._id}
              carrier={carrier}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
      {/* PAGINATION */}
      {!loading && (
        <Pagination
          pagination={pagination}
          page={page}
          onPageChange={setPage}
        />
      )}
      <EditCarrierModal
        modal={carrierModal}
        onCarrierupdated={handleCarrierUpdate}
      />{" "}
    </div>
  );
};

/* Empty State */
const EmptyState = ({ search }) => (
  <div className="col-span-full flex items-center justify-center py-24">
    <div className="max-w-sm w-full border border-dashed border-borderLight bg-surfaceAccentLight/40 rounded-xl p-10 flex flex-col items-center text-center">
      {/* ICON */}
      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent mb-4">
        <PackageSearch size={28} />
      </div>

      {/* TITLE */}
      <p className="text-lg font-semibold text-headingText">
        No carriers found
      </p>

      {/* DESCRIPTION */}
      <p className="text-sm text-descriptionText mt-2 leading-relaxed">
        {search
          ? `No results for "${search}". Try using a different keyword.`
          : "You haven't added any products yet. Start by creating your first product."}
      </p>

      {/* ACTION (only when empty without search) */}
      {!search && (
        <button
          className="mt-5 px-5 py-2 bg-btnColor
           text-white text-sm font-medium rounded-md hover:bg-btnHoverColor transition"
          onClick={() =>
            document.dispatchEvent(new CustomEvent("create-project"))
          }
        >
          + Create Carrier
        </button>
      )}
    </div>
  </div>
);

/*  Skeleton card */
const SkeletonCard = () => (
  <div className="border border-borderLight bg-surfaceColor overflow-hidden animate-pulse">
    <div className="aspect-[4/3] bg-divider" />
    <div className="p-4 space-y-2">
      <div className="h-3 bg-divider rounded w-3/4" />
      <div className="h-3 bg-divider rounded w-full" />
      <div className="h-3 bg-divider rounded w-1/2" />
    </div>
  </div>
);

export default Carrier;
